import React, { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import type { Staff, StaffImpersonation } from '../types/staff'
import { staffApi } from '../services/staffApi'

interface StaffAuthState {
  isLoading: boolean
  isAuthenticated: boolean
  staff: Staff | null
  impersonation: StaffImpersonation | null
}

interface StaffAuthContextType extends StaffAuthState {
  logout: () => Promise<void>
  refreshSession: () => Promise<void>
  endImpersonation: () => Promise<void>
}

const StaffAuthContext = createContext<StaffAuthContextType | null>(null)

export function useStaffAuth(): StaffAuthContextType {
  const context = useContext(StaffAuthContext)
  if (!context) {
    throw new Error('useStaffAuth must be used within a StaffAuthProvider')
  }
  return context
}

interface StaffAuthProviderProps {
  children: ReactNode
}

export function StaffAuthProvider({ children }: StaffAuthProviderProps): React.ReactElement {
  const [state, setState] = useState<StaffAuthState>({
    isLoading: true,
    isAuthenticated: false,
    staff: null,
    impersonation: null,
  })

  const refreshSession = useCallback(async () => {
    try {
      const session = await staffApi.getSession()
      setState({
        isLoading: false,
        isAuthenticated: session.authenticated,
        staff: session.staff || null,
        impersonation: session.impersonation || null,
      })
    } catch {
      setState({
        isLoading: false,
        isAuthenticated: false,
        staff: null,
        impersonation: null,
      })
    }
  }, [])

  useEffect(() => {
    refreshSession()
  }, [refreshSession])

  const logout = useCallback(async () => {
    await staffApi.logout()
    setState({
      isLoading: false,
      isAuthenticated: false,
      staff: null,
      impersonation: null,
    })
  }, [])

  const endImpersonation = useCallback(async () => {
    await staffApi.endImpersonation()
    await refreshSession()
  }, [refreshSession])

  const value: StaffAuthContextType = {
    ...state,
    logout,
    refreshSession,
    endImpersonation,
  }

  return <StaffAuthContext.Provider value={value}>{children}</StaffAuthContext.Provider>
}
